<template>
  <v-card class="elevation-1">
    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
      
      <div v-if="!loading" class="map-container">
        <table class="config-map-table">
          <thead>
            <tr>
              <th class="entity-header sticky-col" rowspan="2">{{ $t('group_user') }}</th>
              <th 
                v-for="pool in filteredPools" 
                :key="`pool-${pool.pool_id}`"
                :colspan="getPoolLanesCount(pool.pool_id) || 1"
                class="pool-header"
              >
                {{ pool.pool_title }}
              </th>
            </tr>
            <tr>
              <template v-for="pool in filteredPools">
                <th 
                  v-if="getPoolLanes(pool.pool_id).length === 0"
                  :key="`pool-empty-${pool.pool_id}`"
                  class="lane-header pool-acl-col"
                >
                  {{ $t('pool_acl') }}
                </th>
                <template v-else>
                  <th 
                    :key="`pool-acl-${pool.pool_id}`"
                    class="lane-header pool-acl-col"
                  >
                    {{ $t('pool_acl') }}
                  </th>
                  <th 
                    v-for="lane in getPoolLanes(pool.pool_id)" 
                    :key="`lane-${lane.lane_id}`"
                    class="lane-header lane-executor-col"
                  >
                    {{ getLaneTitle(lane) }}
                  </th>
                </template>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entity in filteredEntities" :key="entity.key">
              <td class="entity-cell sticky-col">
                <v-chip small :color="entity.type === 'group' ? 'grey lighten-1' : 'secondary'">
                  <v-icon small left>
                    {{ entity.type === 'group' ? 'mdi-account-multiple' : 'mdi-account' }}
                  </v-icon>
                  {{ entity.display }}
                </v-chip>
              </td>
              <template v-for="pool in filteredPools">
                <template v-if="getPoolLanes(pool.pool_id).length === 0">
                  <td 
                    :key="`${entity.key}-pool-${pool.pool_id}`"
                    class="acl-cell pool-acl-col"
                  >
                    <div class="acl-chips">
                      <v-chip
                        v-for="(chip, idx) in getPoolAclForEntity(pool.pool_id, entity)"
                        :key="idx"
                        x-small
                        class="ma-1"
                        :color="chip.color"
                        :text-color="chip.textColor"
                      >
                        {{ chip.text }}
                      </v-chip>
                    </div>
                  </td>
                </template>
                <template v-else>
                  <td 
                    :key="`${entity.key}-pool-${pool.pool_id}`"
                    class="acl-cell pool-acl-col"
                  >
                    <div class="acl-chips">
                      <v-chip
                        v-for="(chip, idx) in getPoolAclForEntity(pool.pool_id, entity)"
                        :key="idx"
                        x-small
                        class="ma-1"
                        :color="chip.color"
                      >
                        {{ chip.text }}
                      </v-chip>
                    </div>
                  </td>
                  <td 
                    v-for="lane in getPoolLanes(pool.pool_id)"
                    :key="`${entity.key}-lane-${lane.lane_id}`"
                    class="executor-cell lane-executor-col"
                  >
                    <v-icon 
                      v-if="isLaneExecutor(lane.lane_id, entity)"
                      color="green"
                    >
                      mdi-check-circle
                    </v-icon>
                    <v-icon
                      v-else-if="hasActPermission(pool.pool_id, entity)"
                      color="orange"
                    >
                      mdi-check-circle
                    </v-icon>
                    <span v-else class="grey--text text--lighten-1">—</span>
                  </td>
                </template>
              </template>
            </tr>
          </tbody>
        </table>

        <div v-if="filteredEntities.length === 0" class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1">mdi-information-outline</v-icon>
          <p class="mt-4 grey--text">{{ $t('no_data_available') }}</p>
        </div>
      </div>

      <!-- Legend and Export Button -->
      <div class="d-flex mt-4" style="gap: 16px;">
        <div style="flex: 1; position: relative; padding-top: 12px;">
          <span class="legend-title">{{ $t('legend') }}</span>
          <v-card outlined>
            <v-card-text>
            <div class="d-flex flex-wrap" style="gap: 16px;">
              <div class="legend-item">
                <v-chip x-small color="grey lighten-1" class="mr-2">VIEW ALL</v-chip>
                <span class="caption">View all lanes with details</span>
              </div>
              <div class="legend-item">
                <v-chip x-small color="grey lighten-1" class="mr-2">VIEW BLUR</v-chip>
                <span class="caption">View all lanes without details</span>
              </div>
              <div class="legend-item">
                <v-chip x-small color="grey lighten-1" class="mr-2">VIEW OWN</v-chip>
                <span class="caption">View lanes with my own jobs</span>
              </div>
              <div class="legend-item">
                <v-chip x-small color="green lighten-2" class="mr-2">SEND</v-chip>
                <span class="caption">{{ $t('sent_job_to_pool') }}</span>
              </div>
              <div class="legend-item">
                <v-chip x-small color="orange lighten-2" class="mr-2">ACT</v-chip>
                <span class="caption">{{ $t('act_in_all_lanes') }}</span>
              </div>
              <div class="legend-item">
                <v-chip x-small color="purple lighten-2" class="mr-2">CREATE SUB</v-chip>
                <span class="caption">{{ $t('create_subtask') }}</span>
              </div>
              <div class="legend-item">
                <v-chip x-small color="purple lighten-2" class="mr-2">VIEW SUB</v-chip>
                <span class="caption">{{ $t('view_subtasks') }}</span>
              </div>
              <div class="legend-item">
                <v-icon color="green" small class="mr-2">mdi-check-circle</v-icon>
                <span class="caption">{{ $t('lane_executor') }}</span>
              </div>
              <div class="legend-item">
                <v-icon color="orange" small class="mr-2">mdi-check-circle</v-icon>
                <span class="caption">{{ $t('act_in_all_lanes') }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
        </div>
        <v-btn
          color="primary"
          @click="exportCSV"
          :disabled="loading || filteredEntities.length === 0"
          style="align-self: flex-start;"
        >
          <v-icon left>mdi-download</v-icon>
          {{ $t('export') }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.map-container {
  overflow: auto;
  max-width: 100%;
  max-height: calc(100vh - 400px);
  position: relative;
}

.config-map-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.config-map-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.config-map-table th,
.config-map-table td {
  border: 1px solid #e0e0e0;
  padding: 8px;
  text-align: center;
  min-width: 80px;
}

.entity-header {
  background-color: #42a5f5;
  color: white;
  font-weight: 600;
  text-align: center !important;
  min-width: 200px !important;
  vertical-align: middle;
}

.pool-header {
  background-color: #42a5f5;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
}

.lane-header {
  background-color: #42a5f5 !important;
  color: white;
  font-size: 0.8rem;
  font-weight: 500;
}

.pool-acl-col {
  background-color: #e3f2fd;
}

.lane-executor-col {
  background-color: #fafafa;
}

/* Override column backgrounds for header row */
thead .pool-acl-col {
  background-color: #42a5f5 !important;
}

thead .lane-executor-col {
  background-color: #42a5f5 !important;
}

.entity-cell {
  text-align: center !important;
  font-weight: 500;
  background-color: #fafafa;
}

.acl-cell {
  vertical-align: top;
  min-width: 120px;
}

.acl-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  min-height: 40px;
}

.executor-cell {
  vertical-align: middle;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: #fafafa;
}

.config-map-table thead th.sticky-col {
  z-index: 3;
  background-color: #42a5f5 !important;
}

.legend-title {
  position: absolute;
  top: 0;
  left: 16px;
  background-color: white;
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  z-index: 1;
}
</style>

<script>
import PoolAPI from '@/services/api/job/pool'
import LaneAPI from '@/services/api/job/lane'
import PoolACLAPI from '@/services/api/job/pool_acl'
import LaneExecutorAPI from '@/services/api/job/lane_executor'
import GroupAPI from '@/services/api/core/group'
import UserAPI from '@/services/api/core/user'

export default {
  name: "MapByAssignment",

  props: {
    appTenantId: {
      type: [String, Number],
      default: null
    }
  },

  computed: {
    // Get all unique entities (groups/users) that appear in pool_acl or lane_executor
    allEntities() {
      const entityMap = new Map()

      // Add entities from pool_acl
      this.poolAcls.forEach(acl => {
        if (acl.user_group_id) {
          const key = `group_${acl.user_group_id}`
          if (!entityMap.has(key)) {
            const group = this.userGroups.find(g => g.user_group_id === acl.user_group_id)
            if (group) {
              entityMap.set(key, {
                key,
                type: 'group',
                id: acl.user_group_id,
                display: group.group_title,
                email: null
              })
            }
          }
        } else if (acl.users_id) {
          const key = `user_${acl.users_id}`
          if (!entityMap.has(key)) {
            const user = this.users.find(u => u.users_id === acl.users_id)
            if (user) {
              entityMap.set(key, {
                key,
                type: 'user',
                id: acl.users_id,
                display: user.name,
                email: user.email
              })
            }
          }
        }
      })

      // Add entities from lane_executor
      this.laneExecutors.forEach(executor => {
        if (executor.user_group_id) {
          const key = `group_${executor.user_group_id}`
          if (!entityMap.has(key)) {
            const group = this.userGroups.find(g => g.user_group_id === executor.user_group_id)
            if (group) {
              entityMap.set(key, {
                key,
                type: 'group',
                id: executor.user_group_id,
                display: group.group_title,
                email: null
              })
            }
          }
        } else if (executor.users_id) {
          const key = `user_${executor.users_id}`
          if (!entityMap.has(key)) {
            const user = this.users.find(u => u.users_id === executor.users_id)
            if (user) {
              entityMap.set(key, {
                key,
                type: 'user',
                id: executor.users_id,
                display: user.name,
                email: user.email
              })
            }
          }
        }
      })

      return Array.from(entityMap.values()).sort((a, b) => {
        // Sort groups first, then users; alphabetically within each type
        if (a.type !== b.type) {
          return a.type === 'group' ? -1 : 1
        }
        return a.display.localeCompare(b.display)
      })
    },

    filteredEntities() {
      if (!this.search) return this.allEntities
      
      const searchLower = this.search.toLowerCase()
      return this.allEntities.filter(entity => 
        entity.display.toLowerCase().includes(searchLower) ||
        (entity.email && entity.email.toLowerCase().includes(searchLower))
      )
    },

    filteredPools() {
      return this.pools
    }
  },

  data() {
    return {
      loading: false,
      search: '',
      pools: [],
      lanes: [],
      poolAcls: [],
      laneExecutors: [],
      userGroups: [],
      users: []
    }
  },

  mounted() {
    this.loadAllData()
  },

  methods: {
    async loadAllData() {
      this.loading = true

      try {
        // Load groups and users first
        const [groupsResult, usersResult] = await Promise.all([
          GroupAPI.getAllGroups({ order: 'group_title', direction: 'ASC', offset: 0, limit: 2000000000 }),
          UserAPI.getAllUsers({ order: 'u.name', direction: 'ASC', offset: 0, limit: 2000000000 })
        ])

        this.userGroups = (groupsResult.data.items || []).map(g => ({
          user_group_id: g.user_group_id || g.id,
          group_title: g.group_title || g.title || g.name
        }))

        this.users = (usersResult.data.items || []).map(u => ({
          users_id: u.users_id || u.id,
          name: u.name,
          email: u.email || null
        }))

        // Load pools
        const poolsParams = { order: 'pool_title', direction: 'ASC', offset: 0, limit: 2000000000 }
        if (this.appTenantId) {
          poolsParams['filter[tenant]'] = this.appTenantId
        }
        const poolsResult = await PoolAPI.getAllPools(poolsParams)
        this.pools = poolsResult.data.items || []

        // Load lanes for all pools
        const lanePromises = this.pools.map(pool => 
          LaneAPI.getLanesFromPool(pool.pool_id).catch(() => ({ data: { items: [] } }))
        )
        const laneResults = await Promise.all(lanePromises)
        this.lanes = laneResults.flatMap(result => result.data.items || [])

        // Load pool ACLs for all pools
        const poolAclPromises = this.pools.map(pool =>
          PoolACLAPI.getPoolACLsFromPool(pool.pool_id).catch(() => ({ data: { items: [] } }))
        )
        const poolAclResults = await Promise.all(poolAclPromises)
        this.poolAcls = poolAclResults.flatMap(result => result.data.items || [])

        // Load lane executors for all lanes
        const executorPromises = this.lanes.map(lane =>
          LaneExecutorAPI.getLaneExecutorsFromLane(lane.lane_id).catch(() => ({ data: { items: [] } }))
        )
        const executorResults = await Promise.all(executorPromises)
        this.laneExecutors = executorResults.flatMap(result => result.data.items || result.data || [])

      } catch (error) {
        console.error('Error loading configuration map data:', error)
      } finally {
        this.loading = false
      }
    },

    getPoolLanes(poolId) {
      return this.lanes.filter(lane => lane.pool_id === poolId)
    },

    getPoolLanesCount(poolId) {
      const count = this.getPoolLanes(poolId).length
      return count === 0 ? 1 : count + 1 // +1 for pool_acl column
    },

    getLaneTitle(lane) {
      // If lane has a title, use it
      if (lane.lane_title) {
        return lane.lane_title
      }
      // Otherwise, construct a title from user group
      const group = this.userGroups.find(g => g.user_group_id === lane.user_group_id)
      if (group) {
        return `Lane: ${group.group_title}`
      }
      // Fallback to lane ID
      return `Lane ${lane.lane_id}`
    },

    getPoolAclForEntity(poolId, entity) {
      const acl = this.poolAcls.find(acl => {
        if (entity.type === 'group') {
          return acl.pool_id === poolId && acl.user_group_id === entity.id
        } else {
          return acl.pool_id === poolId && acl.users_id === entity.id
        }
      })

      if (!acl || !acl.acl) return []

      const chips = []
      const aclValue = acl.acl

      // View permissions (mutually exclusive)
      if (aclValue & 4) {
        chips.push({ text: 'VIEW ALL', color: 'grey lighten-1' })
      } else if (aclValue & 8) {
        chips.push({ text: 'VIEW BLUR', color: 'grey lighten-1' })
      } else if (aclValue & 16) {
        chips.push({ text: 'VIEW OWN', color: 'grey lighten-1' })
      }

      // Additional permissions
      if (aclValue & 1) {
        chips.push({ text: 'SEND', color: 'green lighten-2', textColor: 'black' })
      }
      if (aclValue & 2) {
        chips.push({ text: 'ACT', color: 'orange lighten-2', textColor: 'black' })
      }
      if (aclValue & 32) {
        chips.push({ text: 'CREATE SUB', color: 'purple lighten-2' })
      }
      if (aclValue & 64) {
        chips.push({ text: 'VIEW SUB', color: 'purple lighten-2' })
      }
      if (aclValue & 1024) {
        chips.push({ text: 'FINISH', color: 'blue lighten-2', textColor: 'black' })
      }

      return chips
    },

    isLaneExecutor(laneId, entity) {
      return this.laneExecutors.some(executor => {
        if (entity.type === 'group') {
          return executor.lane_id == laneId && executor.user_group_id == entity.id
        } else {
          return executor.lane_id == laneId && executor.users_id == entity.id
        }
      })
    },

    hasActPermission(poolId, entity) {
      const acl = this.poolAcls.find(acl => {
        if (entity.type === 'group') {
          return acl.pool_id === poolId && acl.user_group_id === entity.id
        } else {
          return acl.pool_id === poolId && acl.users_id === entity.id
        }
      })

      if (!acl || !acl.acl) return false

      // Check if has ACT permission (bit 2)
      return (acl.acl & 2) !== 0
    },

    exportCSV() {
      // Prepare CSV data
      const rows = []
      
      // Build header row 1 (Pool titles)
      const header1 = [this.$t('group_user')]
      this.filteredPools.forEach(pool => {
        const lanesCount = this.getPoolLanesCount(pool.pool_id)
        header1.push(pool.pool_title)
        // Add empty cells for remaining columns in this pool
        for (let i = 1; i < lanesCount; i++) {
          header1.push('')
        }
      })
      rows.push(header1)

      // Build header row 2 (Pool ACL and Lane titles)
      const header2 = ['']
      this.filteredPools.forEach(pool => {
        const poolLanes = this.getPoolLanes(pool.pool_id)
        header2.push(this.$t('pool_acl'))
        
        poolLanes.forEach(lane => {
          header2.push(this.getLaneTitle(lane))
        })
      })
      rows.push(header2)

      // Build data rows
      this.filteredEntities.forEach(entity => {
        const row = [entity.display]
        
        this.filteredPools.forEach(pool => {
          const poolLanes = this.getPoolLanes(pool.pool_id)
          
          // Pool ACL column
          const aclChips = this.getPoolAclForEntity(pool.pool_id, entity)
          const aclText = aclChips.map(chip => chip.text).join(', ')
          row.push(aclText)
          
          // Lane executor columns
          poolLanes.forEach(lane => {
            const isExecutor = this.isLaneExecutor(lane.lane_id, entity)
            row.push(isExecutor ? 'Yes' : 'No')
          })
        })
        
        rows.push(row)
      })

      // Convert to CSV string
      const csvContent = rows.map(row => 
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const cellStr = String(cell || '')
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`
          }
          return cellStr
        }).join(',')
      ).join('\n')

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', `assignment_map_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>
